import { isHouseholdPremium } from '@/services/premium.service'
import { isLegacyCohort, type CohortHousehold } from '@/services/businessCohort'

/**
 * Qui a droit à quoi, selon la cohorte commerciale du foyer.
 *
 * Deux questions vivent ici, et elles sont distinctes :
 *
 *   « ce profil est-il ouvert ? »        -> childHasAccess()
 *   « cette fonctionnalité est-elle
 *     ouverte à ce foyer ? »             -> canUseFeature()
 *
 * Les deux cohortes y répondent en miroir :
 *
 *                     | legacy                | child_limit
 *   ------------------|-----------------------|----------------------
 *   profil ouvert ?   | toujours              | 1er enfant, ou abonné
 *   fonctionnalité ?  | abonné (comme avant)  | toujours
 *
 * Deux fonctionnalités restent en dehors de tout ça et demandent un abonnement
 * à TOUT LE MONDE, les deux cohortes confondues : les avatars premium et les
 * conseils de la chouette. Elles ne passent pas par ce fichier et continuent de
 * lire `isPremium`.
 *
 * Le miroir en base est `child_has_full_access()` / `child_feature_allowed()`,
 * qui restent l'autorité : le client ne fait qu'éviter d'afficher des portes
 * que le serveur refuserait. Les deux DOIVENT trier les enfants pareil, sinon
 * ils ne désignent pas le même enfant gratuit.
 */

/** Le strict nécessaire pour décider, pour que les tests n'aient pas à bâtir un Child complet. */
export interface AccessChild {
  id: string
  created_at?: number | string | null
}

export interface AccessHousehold extends CohortHousehold {
  is_premium: boolean
  premium_until: string | null
}

/**
 * Ordre de création, du plus ancien au plus récent.
 *
 * L'`id` départage à date égale, et il départage plus souvent qu'on ne croit :
 * les enfants d'un onboarding partent dans le même INSERT et partagent la
 * seconde. Sans ce second critère, deux appareils pourraient désigner deux
 * enfants gratuits différents pour le même foyer.
 */
function byCreation(a: AccessChild, b: AccessChild): number {
  const ta = toTime(a.created_at)
  const tb = toTime(b.created_at)
  if (ta !== tb) return ta - tb
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0
}

/**
 * Une date absente ne doit jamais passer devant une date connue : elle vaut
 * l'infini, pas zéro. Un enfant sans `created_at` récupérerait sinon la
 * gratuité au détriment du plus ancien.
 */
function toTime(value: number | string | null | undefined): number {
  if (value === null || value === undefined) return Number.POSITIVE_INFINITY
  const time = typeof value === 'number' ? value : Date.parse(value)
  return Number.isNaN(time) ? Number.POSITIVE_INFINITY : time
}

/**
 * L'enfant qui reste ouvert quand un foyer `child_limit` n'est pas abonné : le
 * plus ancien.
 *
 * La notion n'existe pas pour un foyer `legacy`, qui n'a jamais eu de limite :
 * il rend null, comme `household_free_child_id()` en base.
 */
export function freeChildId(
  children: readonly AccessChild[],
  household: AccessHousehold | null,
): string | null {
  if (isLegacyCohort(household)) return null
  if (children.length === 0) return null
  return [...children].sort(byCreation)[0].id
}

/**
 * Ce profil est-il ouvert ?
 *
 * Un enfant verrouillé garde toutes ses données : il n'est pas supprimé, il est
 * mis en pause. S'abonner rouvre tout sans migration ni réparation, par le seul
 * retour de `is_premium` à true.
 */
export function childHasAccess(
  childId: string,
  children: readonly AccessChild[],
  household: AccessHousehold | null,
): boolean {
  // Foyer pas encore chargé : on ne décide pas. Les écrans traitent ce cas
  // comme un chargement, pas comme un verrou.
  if (!household) return false
  // Un foyer d'avant le modèle par profil n'a aucune limite d'enfants, et n'en
  // aura jamais : c'est le contrat qu'il a accepté.
  if (isLegacyCohort(household)) return true
  if (isHouseholdPremium(household)) return true

  const child = children.find((candidate) => candidate.id === childId)
  if (!child) return false

  return freeChildId(children, household) === childId
}

/**
 * Cette fonctionnalité avancée est-elle ouverte à ce foyer ?
 *
 * Points personnalisés, tâches bonus, routines, argent de poche, espace enfant,
 * séries, historique des bilans. Pour un foyer `legacy`, la réponse est celle
 * d'avant, mot pour mot : il faut être abonné. Pour un foyer `child_limit`,
 * tout cela fait partie du produit gratuit.
 *
 * Miroir de `household_can_use_premium_feature()` en base.
 */
export function canUseFeature(household: AccessHousehold | null): boolean {
  if (!household) return false
  if (!isLegacyCohort(household)) return true
  return isHouseholdPremium(household)
}

/** Les profils en pause, pour les afficher comme tels au lieu de les bloquer en silence. */
export function lockedChildIds(
  children: readonly AccessChild[],
  household: AccessHousehold | null,
): string[] {
  return children
    .filter((child) => !childHasAccess(child.id, children, household))
    .map((child) => child.id)
}

/**
 * Le foyer peut-il accueillir un parent de plus sans payer ?
 *
 * Toujours oui pour un foyer `legacy`, qui n'a jamais eu de limite de parents.
 * Le gratuit du modèle par profil n'en porte qu'un : c'est le second qui
 * demande Harmonia+.
 *
 * Miroir de `household_can_add_parent()` en base. Le compte porte sur les
 * parents DÉJÀ inscrits, propriétaire compris.
 */
export function canAddParentForFree(
  parentCount: number,
  household: AccessHousehold | null,
): boolean {
  if (!household) return false
  if (isLegacyCohort(household)) return true
  if (isHouseholdPremium(household)) return true
  return parentCount < 1
}

/**
 * Le foyer peut-il créer un enfant de plus sans payer ?
 *
 * Toujours oui pour un foyer `legacy`. Pour un foyer `child_limit`, seulement
 * s'il n'a encore aucun enfant, ou s'il est abonné.
 */
export function canAddChildForFree(
  children: readonly AccessChild[],
  household: AccessHousehold | null,
): boolean {
  if (!household) return false
  if (isLegacyCohort(household)) return true
  if (isHouseholdPremium(household)) return true
  return children.length === 0
}
